import axios, { AxiosResponse } from 'axios';
import { logError } from '../utils/errorLogger';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Simple API Client for debugging
class SimpleAPIClient {
  private client = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 120000, // 120 seconds (2 minutes) timeout for large operations
    withCredentials: true, // Enable cookies to be sent with requests
  });
  
  constructor() {
    this.setupInterceptors();
  }
  
  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Ensure cookies are sent with all requests
        config.withCredentials = true;
        
        // Note: Tokens are now in httpOnly cookies, which are sent automatically
        // Authorization header is no longer needed for cookie-based authentication
        
        return config;
      },
      (error) => {
        logError('Request interceptor error', error);
        return Promise.reject(error);
      }
    );
    
    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        logError('API Error', error);
        return Promise.reject(error);
      }
    );
  }
  
  async get<T = any>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.client.get<T>(url, config);
  }
  
  async post<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.client.post<T>(url, data, config);
  }
  
  async put<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.client.put<T>(url, data, config);
  }
  
  async patch<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.client.patch<T>(url, data, config);
  }
  
  async delete<T = any>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.client.delete<T>(url, config);
  }
}

// Export singleton instance
export const simpleApiClient = new SimpleAPIClient();
export default simpleApiClient;
